/* Conjurer.js */

const EventEmitter = require("events").EventEmitter;
const util = require("util");
const Q = require("q");
const $ = require("jquery");
const _ = require("lodash");
const underscoreString = require("underscore.string");
_.mixin(underscoreString.exports());
const CONTROL_TYPE = require("../typedefinitions/formcontrol/FormControlType.enum");
const UNDERLING_TYPE = require("../typedefinitions/formcontrol/UnderlingType.enum");
const VALUE_SCOPE_TYPE = require("../typedefinitions/formcontrol/ValueScope.type");
const PowerButton = require("@gluenetworks/input_powerbutton_control");
const MultiEditHandler = require("./MultiEditHandler");
const StyleUtil = require("./StyleUtil");

/***************************************************************************************/
/*** START EVENT DEFINITION                                                          ***/
/***************************************************************************************/
/**
 * @event Conjurer#AllControlsInitialized
 * @property controlName {string} The name of this control.
 * @property containerElement {DomElement} The Dom element that the control was rendered inside.
 * @property controlAdapter {ControlAdapter} The Control Adapter used to render the control
 * @property formControlDefinition {FormControlDefinition} The definition of the control.
 */

/**
 * @event Conjurer#AllControlsLoaded
 */

/**
 * @event Conjurer#ValueChanged
 * @type {Object}
 * @property [controlValueChangeEvent] {ControlAdapterInterface#ValueChanged} The ControlAdapter ValueChanged event that caused the value change.
 * @property oldValue {*} The previous value
 * @property newValue {*} The new value
 */

/**
 * @event Conjurer#TearDownComplete
 * @type {Object}
 * @property valueBeforeTearDown {Object} The value right before tearDown.
 */
/***************************************************************************************/
/*** END EVENT DEFINITION                                                            ***/
/*** START CLASS DEFINITION                                                          ***/
/***************************************************************************************/

/**
 * @class Conjurer
 * @implements ConjurerInterface
 *
 * @param logger {Bunyan} The logger
 * @param containerElem {DomElement} The dom element this conjurer owns.
 * @param [options] {object} The optional parameters of this Conjurer
 *      @param [options.configurationData] {ConfigurationData} The configuration data used to render the values in this form
 *      @param [options.configurationSettings] {ConfigurationSettings} The type information necessary to conjur up this form.
 *      @param [options.refInstances] {RefInstances} The Ref Instances needed for this Forms configurationSettings
 *      @param [options.refTypes] {RefTypes} The Ref Types needed for this Forms configurationSettings
 *      @param [options.controlAdapterRegistry=AngularControlAdapterRegistry] {ControlAdapterRegistry} An object that represents a ControlAdapterRegistry.
 *      @param [options.render=false] Will force a render upon intialization.
 *
 * @fires AllControlsInitialized
 * @fires AllControlsLoaded
 * @fires ValueChanged
 * @fires TearDownComplete
 */
module.exports = function (logger, containerElem, opts) {
    var self = this;
    //call Event Emitter Constructor
    EventEmitter.call(this);
    var formRendererUtilities = new (require("./FormRendererUtilities"))(logger);

    //Setup private variables
    var _controlAdapterRegistry = require("./ControlAdapterRegistry");

    /**
     * @typeDef Underling
     * @param [type=UnderlingType.CONTROL_ADAPTER] {UnderlingType} The type of this underling
     * @param name {string} The name of this underling
     * @param containerElement {DOMElement} The containing Dom Element
     * @param formControlDefinition {FormControlDefinition} The form control definition this underling represents
     */
    /**
     * @type {Array.<Underling>}
     * @description An array of the underlings that manage each of this forms controls
     * @private
     */
    var _underlings = [];

    //Setup immutable properties
    var _configurationSettings = {};
    var _orderedProps = [];
    var _configurationData = {};
    var _refInstances = {};
    var _displayModeOptions = {};
    var _layoutConfig = {};
    var _styleConfig = {};
    var _refTypes = {};
    var _oldValue;
    var _oldMultiEditActive;

    //Set the default options for the conjurer
    var _conjurerOptions = _.defaults({}, opts, {
        /**
         * Create or retreive the containing element for the given form control definition.
         * @param parentElem {DomElement} The Parent element that fcd is contained within
         * @param fcd {FormControlDefinition} The form control definition that will be contained.
         * @returns {DomElement} The dom element passed to the form control's adapter class
         */
        createAdapterContainingElement: function (parentElem, fcd) {
            var classStr = "rendered_control";
            var typeAsClassName = _.underscored(fcd.type);
            classStr += " " + typeAsClassName;
            var nameAsClassName = _.underscored(fcd.name);
            classStr += " " + nameAsClassName;

            //add required class
            if (fcd.validationInfo && fcd.validationInfo.required) {
                classStr += " required";
            }

            //If the fcd is hidden
            if (fcd.configSettings.Display && fcd.configSettings.Display.hidden) {
                classStr += " hidden";
            }

            var indent = "";
            var startTextTag = "<label";
            var endTextTag = "</label>";
            //If the fcd display indent
            if (fcd.configSettings.Display && fcd.configSettings.Display.formatLabel) {
                startTextTag = "<pre";
                endTextTag = "</pre>";
            }

            var ctrlElem = $(
                '<div class="' +
                    classStr +
                    '">' +
                    startTextTag +
                    ' title="' +
                    fcd.displayInfo.tooltip +
                    '">' +
                    indent +
                    fcd.displayInfo.label +
                    endTextTag +
                    "</div>"
            );

            parentElem.append(ctrlElem);
            return ctrlElem;
        },
    });

    /***************************************************************************************/
    /*** START PUBLIC API                                                                ***/
    /***************************************************************************************/
    /**
     * Set the current configuration data of this form. This represents the currently "Saved" data of this form,
     * it does not reflect what is currently visible in the form for that @see ArchMageInterface.setValue().
     * @param configData {ConfigurationData} The configuration data used to render the values in this form.
     * @param [options] {Object} optional arguments
     *     @param [options.syncPowerButtons=false] {boolean} If true the values on the power buttons will also be
     *     updated.
     * @throws {Error} if configData is not a plainObject
     * @memberof Conjurer
     */
    this.setConfigurationData = function (configData, options = {}) {
        const {syncPowerButtons = false} = options || {};
        const silent = !syncPowerButtons;
        if (_.isPlainObject(configData) || _configurationSettings.hasOwnProperty("@")) {
            _configurationData = _.cloneDeep(configData);
            //update the underlings configData
            _underlings.forEach(function (underling) {
                var fcd = underling.formControlDefinition;
                var adapterInfo = fcd.adapterInfo;
                var adptr = adapterInfo.adapter;
                if (adptr) {
                    switch (adapterInfo.valueScope) {
                        case VALUE_SCOPE_TYPE.REPLACE:
                        case VALUE_SCOPE_TYPE.PARENT:
                        case VALUE_SCOPE_TYPE.OBJECT:
                            adptr.setValue(_configurationData, silent);
                            if (!silent) {
                                adptr.emit("ValueRestored");
                            }
                            break;
                        case VALUE_SCOPE_TYPE.TRANSPARENT:
                        case VALUE_SCOPE_TYPE.ISOLATED:
                        default:
                            adptr.setValue(_configurationData[fcd.name], silent);
                            if (!silent) {
                                adptr.emit("ValueRestored");
                            }
                            break;
                    }
                } else {
                    logger.error(
                        underling,
                        "(Conjurer.setConfigurationData) Can't set config data because an adapter doesn't exist."
                    );
                }
            });
        } else {
            var errMsg =
                "(Conjurer.setConfigurationData) param configData must be a plain JS object see docs.";
            logger.warn({configData: configData}, errMsg);
            throw new Error(errMsg);
        }
    };
    /**
     * @returns {ConfigurationData} the current "saved" state of this form. This is not the current values in the form.
     * @memberof Conjurer
     */
    this.getConfigurationData = function () {
        return _configurationData;
    };
    /**
     * @returns {RefInstances} the current set of ref instances associated with this form.
     */
    this.getRefInstances = function () {
        return _refInstances;
    };
    /**
     * Set the current refInstances associated with this form.
     * @param ri {RefInstances} the refInstances to be associated with this form.
     */
    this.setRefInstances = function (ri) {
        _refInstances = ri;
    };
    /**
     * @returns {RefTypes} the current set of ref types associated with this form.
     */
    this.getTypes = function () {
        return _refTypes;
    };
    /**
     * Set the current refTypes associated with this form.
     * @param ri {RefTypes} the refTypes to be associated with this form.
     */
    this.setRefTypes = function (rt) {
        _refTypes = rt;
    };

    /**
     * @returns {ConfigurationSettings} the current type information used to render the form.
     * @memberof Conjurer
     */
    this.getConfigurationSettings = function () {
        return _configurationSettings;
    };
    this.setDisplayModeOptions = function (displayModes) {
        _displayModeOptions = displayModes;
    };

    this.getStyleConfig = function () {
        return _styleConfig;
    };

    /**
     * @returns {ConfigurationData} the current values from within the form.
     * @memberof Conjurer
     */
    this.getValue = function () {
        if (_.get(_displayModeOptions, "multiEditMode", false)) {
            if (this.getMultiEditActive()) {
                return _getValueFromUnderlings();
            } else {
                return undefined;
            }
        } else {
            return _getValueFromUnderlings();
        }
    };

    this.isInvalid = function () {
        let multiEditModeEnabled = _.get(_displayModeOptions, "multiEditMode", false);
        //if multi edit mode is enabled but current component is not multi edit active
        if (multiEditModeEnabled && !this.getMultiEditActive()) {
            return false;
        }
        return _getInvalidStatusFromUnderlings();
    };

    this.isInProgress = function () {
        let multiEditModeEnabled = _.get(_displayModeOptions, "multiEditMode", false);
        //if multi edit mode is enabled but current component is not multi edit active
        if (multiEditModeEnabled && !this.getMultiEditActive()) {
            return false;
        }
        return _underlings.some(function (underling) {
            let fcd = underling.formControlDefinition;
            let adapter = fcd.adapterInfo.adapter;
            return adapter && _.isFunction(adapter.isInProgress) && adapter.isInProgress();
        });
    };

    this.getValidationMessages = function () {
        if (_.get(_displayModeOptions, "multiEditMode", false) && !this.getMultiEditActive()) {
            return [];
        }
        return _getValidationMessagesFromUnderlings();
    };

    this.setReadOnly = function (flag) {
        this._setUnderlingsReadOnly(flag);
    };

    /**
     * This function in used in the multi edit mode.  It will determine the
     * path properties that "Could" be part of an object but are currently not.
     * This will allow the multi-edit to delete existing paths from the objects
     * being updated.
     *
     * @param {string} currentPath The current dot notation to the object.
     */
    this.getNonSelectedPaths = function (path) {
        let currentPath = path;
        if (_.isUndefined(currentPath)) {
            currentPath = "";
        }
        if (this.getMultiEditActive()) {
            return _getNonSelectedPathsFromUnderlings(currentPath);
        } else {
            return [];
        }
    };

    /**
     * Determine is the component is in a multi edit active state.
     */
    this.getMultiEditActive = function () {
        return _getMultiEditActiveFromUnderlings();
    };

    this.setUnderlingsMultiEditActive = function (flag) {
        _underlings.forEach((underling) => {
            var fcd = underling.formControlDefinition;
            var adptr = fcd.adapterInfo.adapter;
            if (adptr) {
                if (_.isFunction(adptr.setMultiEditActive)) {
                    adptr.setMultiEditActive(flag);
                }
            }
        });
    };

    this._setUnderlingsReadOnly = function (flag) {
        _underlings.forEach((underling) => {
            var fcd = underling.formControlDefinition;
            var adptr = fcd.adapterInfo.adapter;
            if (adptr) {
                if (_.isFunction(adptr.setReadOnly)) {
                    adptr.setReadOnly(flag);
                }
            }
        });
    };

    /**
     * Reset the forms currently rendered values to the defaults specified in the current Configuration Settings
     * @memberof Conjurer
     */
    this.resetToDefaultValues = function () {
        var fcds = _parseConfigSettings();
        fcds.forEach(function (fcd) {
            _setConfigDataProperty(fcd.name, fcd.value);
        });
        self.restoreFromConfigurationData();
    };
    /**
     * Restore the form to the last "saved" values. Or in other words populate the form with the current configuration data values.
     * @memberof Conjurer
     */
    this.restoreFromConfigurationData = function () {
        //update the UI
        self.renderValues(_configurationData);
    };
    /**
     * Set the currently visible values in the form do not change the "saved" state of this form.
     * @param value {ConfigurationData} The values used to render the values in this form.
     * @param [silent=true] {boolean} If true does not emit "ValueRestored" event.
     * @throws {Error} if value is not a plain Object
     * @memberof Conjurer
     */
    this.renderValues = function (value, silent = true) {
        if (_.isUndefined(value)) {
            return;
        }
        if (_.isPlainObject(value)) {
            //Set the underling's values
            _underlings.forEach(function (underling) {
                _setUnderlingsValue(underling, value, silent);
            });
        } else {
            var errMsg = "(Conjurer.renderValues) param value must be a plain JS object see docs.";
            logger.warn({value: value}, errMsg);
            throw new Error(errMsg);
        }
    };

    this.selectFirstElement = function () {
        // Select the first input item
        containerElem.find(':text,:checkbox,:input[type="number"]').each(function () {
            if (
                !this.readOnly &&
                !this.disabled &&
                !$(this).parentsUntil("form", "div").is(".ng-hide, .chosen-container") &&
                !$(this).closest("div").hasClass("chosen-search")
            ) {
                this.focus();
                return false;
            }
        });
    };

    /**
     * Render the control.
     * This will render/re-render the form with the current configuration settings.
     * If configuration data is provided it will be used otherwise the default values from the configuration settings
     * will be used. If properties within the current configuration data are missing they will be updated with the
     * defaults from the configSettings.
     * @param configSettings {ConfigurationSettings} The configuration settings or type information needed to render/describe this form.
     * @param [options] {object} optional arguments
     *      @param [options.configurationData=undefined] {ConfigurationData} The configuration data used to render the values in this form from.
     *      @param [options.refInstances=undefined] {RefInstances} The Ref Instances needed for this Forms configurationSettings
     *      @param [options.refTypes=undefined] {RefTypes} The Ref Types needed for this Forms configurationSettings
     * @param keepjTypeContext {boolean} if true will clear jTypeContext on current element on tear down else jTypeContext will be left untouched
     *          false by default
     * @memberof Conjurer
     */
    this.render = function (configSettings, options, keepjTypeContext = false) {
        //If there are underlings first tear them down
        if (!_.isEmpty(_underlings)) {
            self.tearDown(keepjTypeContext);
        }

        //set the configSettings
        _setConfigurationSettings(configSettings);
        //set configurationData if provided
        if (options) {
            if (options.configurationData) {
                self.setConfigurationData(options.configurationData);
            }
            if (options.refInstances) {
                self.setRefInstances(options.refInstances);
            }
            if (options.displayModeOptions) {
                self.setDisplayModeOptions(options.displayModeOptions);
            }
            if (options.refTypes) {
                self.setRefTypes(options.refTypes);
            }
        }
        //grab the Form Control Definitions
        var fcds = _parseConfigSettings();

        var fcdInitiailizedPromises = [];
        var fcdLoadedPromises = [];

        //Render each form control
        fcds.forEach(function (fcd, index) {
            fcd.displayModeOptions = _.get(options, "displayModeOptions", undefined);
            fcd.layoutConfig = _layoutConfig;
            fcd.styleConfig = _styleConfig;
            if (fcd.name === "@VALUE") {
                if (
                    _.isNumber(_configurationData) ||
                    _.isBoolean(_configurationData) ||
                    !_.isEmpty(_configurationData)
                ) {
                    fcd.value = _configurationData;
                    fcd.configData = _configurationData;
                }
            } else {
                //Set the value/_configurationData starting point
                switch (fcd.adapterInfo.valueScope) {
                    case VALUE_SCOPE_TYPE.REPLACE:
                        //fcd.value TODO no clue how to default the value at this point
                        fcd.configData = _configurationData;
                        break;
                    case VALUE_SCOPE_TYPE.PARENT:
                        fcd.value = _configurationData[fcd.name];
                        fcd.configData = _configurationData;
                        break;
                    case VALUE_SCOPE_TYPE.OBJECT:
                        fcd.value = _configurationData[fcd.name];
                        fcd.configData = _configurationData;
                        // Identify the values that are outside the objects scope
                        let excluded = _.clone(_orderedProps);
                        let currentIdx = excluded.indexOf(fcd.name);
                        if (currentIdx > -1) {
                            excluded.splice(currentIdx, 1);
                        }
                        fcd.excludedProps = excluded;
                        break;
                    case VALUE_SCOPE_TYPE.ISOLATED:
                    case VALUE_SCOPE_TYPE.TRANSPARENT:
                    default:
                        if (_configurationData.hasOwnProperty(fcd.name)) {
                            fcd.value = _configurationData[fcd.name];
                            var configDataValue = _configurationData[fcd.name];

                            // Helper function to determine if a value should be treated as undefined
                            var isEmptyValue = function (value, fieldType) {
                                // Empty plain objects should be treated as undefined
                                if (_.isPlainObject(value) && Object.keys(value).length === 0) {
                                    return true;
                                }
                                // Empty arrays for non-array fields should be treated as undefined
                                if (
                                    _.isArray(value) &&
                                    fieldType !== "array" &&
                                    value.length === 0
                                ) {
                                    return true;
                                }
                                return false;
                            };

                            // Assign configData based on value type
                            if (isEmptyValue(configDataValue, fcd.type)) {
                                // Empty values → undefined for proper initialization
                                fcd.configData = undefined;
                            } else if (
                                _.isPlainObject(configDataValue) ||
                                _.isArray(configDataValue)
                            ) {
                                // Non-empty plain objects and arrays → pass through
                                fcd.configData = configDataValue;
                            } else if (
                                configDataValue === undefined ||
                                configDataValue === null ||
                                typeof configDataValue !== "object"
                            ) {
                                // Primitives, undefined, null → pass through
                                fcd.configData = configDataValue;
                            } else {
                                // Complex objects (JType schemas) → replace with empty object
                                fcd.configData = {};
                            }
                        }
                        //update _configurationData to use the default for this property
                        else {
                            _setConfigDataProperty(fcd.name, fcd.value);
                        }
                        break;
                }
            }

            //produce the containing dom element for the adapter
            var ctrlElem = _createAdapterContainingElement(containerElem, fcd);
            var multiEditElem;

            // If in multi edit mode, check to see if powerbutton needs to be inserted.
            if (_.get(_displayModeOptions, "multiEditMode", false)) {
                let readOnly = false;
                switch (fcd.type) {
                    case CONTROL_TYPE.OBJECT:
                    case CONTROL_TYPE.TAB_GROUP:
                    case CONTROL_TYPE.TAB_OBJECT:
                        // ignore
                        break;
                    default: {
                        let div = document.createElement("div");
                        div.classList.add("multi_edit_select_widget");
                        let multiEditControls = document.createElement("div");
                        multiEditControls.classList.add("multi_edit_controls");
                        div.appendChild(multiEditControls);
                        if (
                            _.get(fcd, "configSettings.Display.readOnly", false) ||
                            _.get(fcd, "configSettings.Display.multiEdit.readOnly", false)
                        ) {
                            readOnly = true;
                            let readOnlyIndicator = document.createElement("base-icon-gl");
                            readOnlyIndicator.classList.add("multi_edit_read_only");
                            readOnlyIndicator.classList.add("multi_edit_symbol");
                            readOnlyIndicator.setAttribute("data-icon", "form.read_only");
                            readOnlyIndicator.setAttribute("title", "Read Only Field");
                            multiEditControls.appendChild(readOnlyIndicator);
                            if (_.get(fcd, "configSettings.Display.multiEdit.readOnly", false)) {
                                readOnlyIndicator.setAttribute("title", "Unique Field");
                                _.set(fcd, "configSettings.Display.readOnly", true);
                            }
                        } else if (fcd.adapterInfo.class.isMultiEditSupported) {
                            let powerButton = new PowerButton();
                            powerButton.setToolTip("Click to enable/disable field for update.");
                            fcd.multiEditHandler = new MultiEditHandler(fcd, powerButton);
                            multiEditControls.appendChild(powerButton.renderer.rootElement);
                            if (
                                _.get(fcd, "configSettings.Display._multiEdit.fieldNotEqual", false)
                            ) {
                                let multiEditIndicators = document.createElement("div");
                                multiEditIndicators.classList.add("multi_edit_indicators");
                                multiEditIndicators.classList.add("web-1-0");
                                multiEditIndicators.innerHTML = "&#8800;";
                                let toolTip = document.createElement("div");
                                toolTip.classList.add("tool_tip");
                                let toolTipHeader = document.createElement("div");
                                toolTipHeader.classList.add("tool_tip_header");
                                toolTip.appendChild(toolTipHeader);
                                toolTipHeader.innerHTML = "Different Values:";
                                let toolTipList = document.createElement("ul");
                                toolTip.appendChild(toolTipList);
                                let diffs = Object.keys(
                                    _.get(fcd, "configSettings.Display._multiEdit.diffs", {})
                                );
                                diffs.forEach((diff) => {
                                    let listItem = document.createElement("li");
                                    listItem.innerText = diff;
                                    toolTipList.appendChild(listItem);
                                });
                                multiEditIndicators.appendChild(toolTip);
                                multiEditControls.appendChild(multiEditIndicators);
                            }
                        } else {
                            let notSupportedIndicator = document.createElement("base-icon-gl");
                            notSupportedIndicator.classList.add("multi_edit_symbol");
                            notSupportedIndicator.setAttribute("data-icon", "form.no_multi_edit");
                            notSupportedIndicator.setAttribute(
                                "title",
                                "Field editing not supported in multi-edit mode."
                            );
                            multiEditControls.appendChild(notSupportedIndicator);
                        }
                        // This is an ugly hack because of the way the tab array is put
                        // together.  We need to attach the multi-edit wrapper to the
                        // tab_container so that it's inside the tab.
                        if (fcd.type === CONTROL_TYPE.TAB_ARRAY) {
                            var tabContainer = $(ctrlElem).children(".tab_container");
                            tabContainer[0].appendChild(div);
                            tabContainer.addClass(" multi_edit");
                            if (readOnly) {
                                tabContainer.addClass("read_only");
                            }
                        } else {
                            let newContainerElem;
                            if (
                                fcd.type === "text" ||
                                (fcd.type === "ref" &&
                                    !(
                                        fcd.configSettings.Ref.many &&
                                        (_.get(fcd, "configSettings.Display.ref") ||
                                            _.get(fcd, "configSettings.Display.pickList"))
                                    )) ||
                                fcd.type === "enum" ||
                                fcd.type === "number"
                            ) {
                                let label = ctrlElem.find(" > label")[0];
                                newContainerElem = document.createElement("div");
                                newContainerElem.appendChild(label);
                                div.appendChild(newContainerElem);
                            }
                            ctrlElem[0].appendChild(div);
                            ctrlElem.addClass(" multi_edit");
                            if (readOnly) {
                                ctrlElem.addClass("read_only");
                            }
                            multiEditElem = ctrlElem;
                            // Set the new ctrlElem to be the multi-edit container.
                            if (newContainerElem) {
                                ctrlElem = $(newContainerElem);
                            } else {
                                ctrlElem = $(div);
                            }
                        }
                    }
                }
            }

            //Load the Control Adapter
            fcd.adapterInfo.adapter = new fcd.adapterInfo.class(logger, ctrlElem, fcd);

            //Register listener for value changes
            fcd.adapterInfo.adapter.on("ValueChanged", function (evt) {
                //update form control definition
                fcd.value = evt.newValue;

                //figure out the new value based on the FCDs
                var newValue = _getValueFromUnderlings();

                fireValueChanged(newValue, evt);
            });

            fcd.adapterInfo.adapter.on("MultiEditActive", function (evt) {
                // If the value has changed emit an event;
                let newMultiEditState = self.getMultiEditActive();
                if (_oldMultiEditActive !== newMultiEditState) {
                    _fireMultiEditActive(newMultiEditState, evt);
                }
            });

            fcd.adapterInfo.adapter.on("Invalid", function (evt) {
                let newInalidStatus = _getInvalidStatusFromUnderlings();
                _fireInvalidValue(newInalidStatus, evt);
            });

            fcd.adapterInfo.adapter.on("InProgress", function (evt) {
                _fireInProgress(evt.newInProgressStatus, evt);
            });

            var initializationDefered = Q.defer();
            fcdInitiailizedPromises.push(initializationDefered.promise);

            //Listen for the Control to finish initialization
            fcd.adapterInfo.adapter.once("InitializationComplete", function (evt) {
                // Register adapter listener for multi-edit handler after adapter is ready
                if (
                    fcd.multiEditHandler &&
                    _.isFunction(fcd.multiEditHandler.registerAdapterListener)
                ) {
                    fcd.multiEditHandler.registerAdapterListener();
                }

                initializationDefered.resolve({
                    formControlDefinition: fcd,
                    newValue: evt.initializedValue,
                });
            });

            var loadedPromise;

            //Initialize the Adapter
            //if is angular control adapter
            if (fcd.adapterInfo.adapter.constructor.isAngularControl) {
                if (_.isFunction(_conjurerOptions.angularControlAdapterInitializer)) {
                    loadedPromise = _conjurerOptions.angularControlAdapterInitializer(
                        ctrlElem,
                        fcd
                    );
                } else {
                    logger.fatal(
                        fcd,
                        "(Conjurer.render) Failed to render angular control for element (" +
                            fcd.name +
                            "), an angularControlAdapterInitializer was not provided."
                    );
                }
            } else {
                fcd.adapterInfo.adapter.init();
            }

            // Check if loadedPromise is a valid promise using duck typing
            // Don't rely on toString() as different promise libraries return different strings
            var hasPromise = loadedPromise && typeof loadedPromise.then === "function";
            if (!hasPromise) {
                loadedPromise = initializationDefered.promise;
            }

            // Track promise for error handling
            var controlInfo = {name: fcd.name, type: fcd.type};
            var trackedPromise = loadedPromise.catch(function (error) {
                logger.error("Control failed to load", {control: controlInfo, error: error});
                throw error;
            });

            fcdLoadedPromises.push(trackedPromise);

            //Add new underling
            _underlings.push({
                type: UNDERLING_TYPE.CONTROL_ADAPTER,
                name: fcd.name,
                containerElement: ctrlElem,
                multiEditElem: multiEditElem,
                formControlDefinition: fcd,
            });
        });
        //When all controls of initialized notify whoever cares
        Q.all(fcdInitiailizedPromises).done(
            function success(values) {
                self.emit("AllControlsInitialized", values);
            },
            function error(err) {
                logger.fatal(
                    err,
                    "(Conjurer.render) Failed to initialize all the control adapters."
                );
            }
        );
        //When all controls are loaded notify whoever cares
        // Q.all(fcdLoadedPromises).done(
        //     function success(values) {
        //         fireValueChanged(self.getValue());
        //         self.emit("AllControlsLoaded", values);
        //     },
        //     function error(err) {
        //         logger.fatal(err, "(Conjurer.render) Failed to load all the control adapters.");
        //     }
        // );

        // Only emit AllControlsLoaded if there are actually controls to load
        if (fcdLoadedPromises.length === 0) {
            // Force emit to ensure loader is hidden even with no controls
            setTimeout(function () {
                fireValueChanged(self.getValue());
                self.emit("AllControlsLoaded", []);
            }, 100);
            return;
        }

        // Wait for all controls to load without timeout
        Promise.all(fcdLoadedPromises)
            .then(function (values) {
                fireValueChanged(self.getValue());
                self.emit("AllControlsLoaded", values);
            })
            .catch(function (err) {
                logger.error("Error loading form controls", err);
                self.emit("AllControlsLoaded", []);
            });
    };
    /**
     * Properly destroy and cleanup all events,dom elements, and js associated with this rendered form.
     * @memberof Conjurer
     */
    this.tearDown = function (keepjTypeContext = false) {
        _configurationSettings = {};
        _configurationData = {};
        var oldVal = _.cloneDeep(_oldValue);
        _oldValue = undefined;

        StyleUtil.clearJTypeClassesDynamicallyAdded(
            containerElem[0],
            _styleConfig,
            _.get(_configurationSettings, "$scope.jTypeContext.layout.container.cssClasses", [])
        );
        //tearDown underlings
        _underlings.forEach(function (underling) {
            var adptr = underling.formControlDefinition.adapterInfo.adapter;
            if (adptr) {
                adptr.tearDown();
                //If this an angular control Adapter properly destroy the scope variable
                if (adptr.constructor.isAngularControl && underling.formControlDefinition.$scope) {
                    if (
                        !keepjTypeContext &&
                        _.get(underling, "formControlDefinition.$scope.$parent.jTypeContext") !==
                            undefined
                    ) {
                        underling.formControlDefinition.$scope.$parent.jTypeContext = undefined;
                    }
                    underling.formControlDefinition.$scope.$destroy();
                }
                //NOT the best coding practice in the world but this will make
                //sure everyone listening to his adapter doesn't receive rogue events
                adptr.removeAllListeners();
            }

            if (underling.formControlDefinition.multiEditHandler) {
                underling.formControlDefinition.multiEditHandler.tearDown();
            }
            if (underling.multiEditElem) {
                underling.multiEditElem.remove();
            } else {
                underling.containerElement.remove();
            }
        });
        _underlings = [];

        // NOTE: Do NOT remove AllControlsLoaded listeners - they are managed by the controller

        //Fire TearDown Event
        self.emit("TearDownComplete", {
            valueBeforeTearDown: oldVal,
        });
    };
    /***************************************************************************************/
    /*** END PUBLIC API                                                                  ***/
    /*** START PRIVATE METHODS                                                           ***/
    /***************************************************************************************/

    /**
     * Determine if the component is active in multi edit mode.
     * @private
     */
    var _getMultiEditActiveFromUnderlings = function () {
        let multiEditActive = false;
        _underlings.forEach(function (underling) {
            var fcd = underling.formControlDefinition;
            var adptr = fcd.adapterInfo.adapter;
            if (_.isFunction(adptr.getMultiEditActive)) {
                if (adptr.getMultiEditActive()) {
                    multiEditActive = true;
                }
            }
        });
        return multiEditActive;
    };

    /**
     * Retreives the value of the Form from the P.O.V of this Conjurer, via the control adapters.
     * @returns {object} The value found from within the form.
     * @private
     */
    var _getValueFromUnderlings = function () {
        var result = {};
        _underlings.forEach(function (underling) {
            var fcd = underling.formControlDefinition;
            var adptr = fcd.adapterInfo.adapter;
            if (adptr) {
                var val = adptr.getValue();
                // If in multi edit mode only get the values from the active components.
                if (_.get(_displayModeOptions, "multiEditMode", false)) {
                    if (_.isFunction(adptr.getMultiEditActive)) {
                        if (!adptr.getMultiEditActive()) {
                            val = undefined;
                        }
                    } else {
                        val = undefined;
                    }
                }
                if (_.isNull(val)) {
                    result[fcd.name] = null;
                } else if (!_.isUndefined(val)) {
                    switch (fcd.adapterInfo.valueScope) {
                        case VALUE_SCOPE_TYPE.REPLACE:
                            var replaceProps = Object.keys(val);
                            if (replaceProps.length) {
                                var replaceProp = replaceProps[0];
                                var replaceVal = val[replaceProp];
                                result[replaceProp] = replaceVal;
                            }
                            break;
                        case VALUE_SCOPE_TYPE.PARENT:
                        case VALUE_SCOPE_TYPE.OBJECT:
                        case VALUE_SCOPE_TYPE.TRANSPARENT:
                            //Parent means merge the underlings value into the parent
                            result = _.extend(result, val);
                            break;
                        case VALUE_SCOPE_TYPE.ISOLATED:
                        default:
                            if (fcd.name === "@Complete") {
                                // This is a special case for @Complete arrays where the
                                // underling value should be returned directly
                                result = val;
                            } else {
                                result[fcd.name] = val;
                            }
                            break;
                    }
                }
            } else {
                logger.error(
                    underling,
                    "(Conjurer._getValueFromUnderlings) Can't get the values because an adapter doesn't exist."
                );
            }
        });
        return result;
    };

    var _getInvalidStatusFromUnderlings = function () {
        let multiEditModeEnabled = _.get(_displayModeOptions, "multiEditMode", false);
        return _underlings.some(function (underling) {
            let fcd = underling.formControlDefinition;
            let adptr = fcd.adapterInfo.adapter;
            if (adptr && _.isFunction(adptr.isInvalid) && adptr.isInvalid()) {
                // If not in multi edit mode or in multi edit mode & component is active in multi edit
                if (
                    !multiEditModeEnabled ||
                    (multiEditModeEnabled &&
                        _.isFunction(adptr.getMultiEditActive) &&
                        adptr.getMultiEditActive())
                ) {
                    return true;
                } else if (multiEditModeEnabled && !_.isFunction(adptr.getMultiEditActive)) {
                    return true;
                }
            }
            return false;
        });
    };

    var _getValidationMessagesFromUnderlings = function () {
        let hasValidationMessages = false;
        let returnMessages = {};
        let multiEditEnabled = _.get(_displayModeOptions, "multiEditMode", false);
        _underlings.forEach(function (underling) {
            var fcd = underling.formControlDefinition;
            var adptr = fcd.adapterInfo.adapter;
            if (adptr) {
                let validationMessage = _.isFunction(adptr.getValidationMessages)
                    ? adptr.getValidationMessages()
                    : [];
                // If in multi edit mode only get the validation messages from the active components.
                if (
                    multiEditEnabled &&
                    _.isFunction(adptr.getMultiEditActive) &&
                    !adptr.getMultiEditActive()
                ) {
                    validationMessage = [];
                } else if (multiEditEnabled && !_.isFunction(adptr.getMultiEditActive)) {
                    validationMessage = [];
                }
                // if non empty validation messages are not found so far & current underling has >0 validation messages
                if (!hasValidationMessages && !_.isEmpty(validationMessage)) {
                    hasValidationMessages = true;
                }
                if (!_.isEmpty(validationMessage)) {
                    switch (fcd.adapterInfo.valueScope) {
                        case VALUE_SCOPE_TYPE.REPLACE:
                            var replaceProps = Object.keys(validationMessage);
                            if (replaceProps.length > 0) {
                                var replaceProp = replaceProps[0];
                                var replaceVal = validationMessage[replaceProp];
                                returnMessages[replaceProp] = replaceVal;
                            }
                            break;
                        case VALUE_SCOPE_TYPE.PARENT:
                        case VALUE_SCOPE_TYPE.OBJECT:
                        case VALUE_SCOPE_TYPE.TRANSPARENT:
                            //Parent means merge the underlings value into the parent
                            returnMessages = _.extend(returnMessages, validationMessage);
                            break;
                        case VALUE_SCOPE_TYPE.ISOLATED:
                        default:
                            if (fcd.name === "@Complete") {
                                // This is a special case for @Complete arrays where the
                                // underling value should be returned directly
                                returnMessages = validationMessage;
                            } else {
                                returnMessages[fcd.name] = validationMessage;
                            }
                            break;
                    }
                }
            } else {
                logger.error(
                    underling,
                    "(Conjurer._getValueFromUnderlings) Can't get the validation messages because an adapter doesn't exist."
                );
            }
        });
        return hasValidationMessages ? returnMessages : [];
    };

    /**
     * Retrieve the non-selected paths from the underlings..
     * @returns {array} an array of all the active possible paths not used.
     * @private
     */
    var _getNonSelectedPathsFromUnderlings = function (path) {
        var result = [];
        _underlings.forEach(function (underling) {
            var fcd = underling.formControlDefinition;
            var adptr = fcd.adapterInfo.adapter;
            if (adptr) {
                var nonSelected = [];
                // If in multi edit mode only get the values from the active components.
                if (_.get(_displayModeOptions, "multiEditMode", false)) {
                    if (_.isFunction(adptr.getMultiEditActive)) {
                        if (adptr.getMultiEditActive()) {
                            if (_.isFunction(adptr.getNonSelectedPaths)) {
                                switch (fcd.adapterInfo.valueScope) {
                                    case VALUE_SCOPE_TYPE.PARENT:
                                    case VALUE_SCOPE_TYPE.TRANSPARENT:
                                    case VALUE_SCOPE_TYPE.REPLACE:
                                    case VALUE_SCOPE_TYPE.OBJECT:
                                        nonSelected = adptr.getNonSelectedPaths(path);
                                        break;
                                    default:
                                        let childPath = "";
                                        if (_.isEmpty(path)) {
                                            childPath = fcd.name;
                                        } else {
                                            childPath = path + "." + fcd.name;
                                        }
                                        nonSelected = adptr.getNonSelectedPaths(childPath);
                                        break;
                                }
                            }
                        }
                    }
                }
                if (!_.isEmpty(nonSelected)) {
                    result = _.union(result, nonSelected);
                }
            } else {
                logger.error(
                    underling,
                    "(Conjurer._get) Can't get the values because an adapter doesn't exist."
                );
            }
        });
        return result;
    };
    /**
     * Retrieves the current value of the Form from the P.O.V of this Conjurer, via the Form Control Definitions.
     * @returns {{}}
     * @private
     */
    var _getValueFromFcds = function () {
        var result = {};
        _underlings.forEach(function (underling) {
            var fcd = underling.formControlDefinition;
            var val = fcd.value;
            if (_.isNull(val)) {
                return null;
            } else if (!_.isUndefined(val)) {
                switch (fcd.adapterInfo.valueScope) {
                    case VALUE_SCOPE_TYPE.REPLACE:
                        var replaceProps = Object.keys(val);
                        if (replaceProps.length) {
                            var replaceProp = replaceProps[0];
                            var replaceVal = val[replaceProp];
                            result[replaceProp] = replaceVal;
                        }
                        break;
                    case VALUE_SCOPE_TYPE.PARENT:
                    case VALUE_SCOPE_TYPE.OBJECT:
                    case VALUE_SCOPE_TYPE.TRANSPARENT:
                        //Parent means merge the underlings value into the parent
                        result = _.extend(result, val);
                        break;
                    case VALUE_SCOPE_TYPE.ISOLATED:
                    default:
                        result[fcd.name] = val;
                        break;
                }
            }
        });
        return result;
    };
    /**
     * Set the value of the underling form control.
     * @param {Object} underling - The underling object.
     * @param {Object} value - The value to set.
     * @param {boolean} [silent=true] - If true does not emit "ValueRestored" event.
     * @private
     */
    var _setUnderlingsValue = function (underling, value, silent = true) {
        var fcd = underling.formControlDefinition;
        var adptr = fcd.adapterInfo.adapter;
        if (adptr) {
            var valToRender;
            switch (fcd.adapterInfo.valueScope) {
                case VALUE_SCOPE_TYPE.REPLACE:
                case VALUE_SCOPE_TYPE.PARENT:
                case VALUE_SCOPE_TYPE.OBJECT:
                    valToRender = value;
                    break;
                case VALUE_SCOPE_TYPE.TRANSPARENT:
                case VALUE_SCOPE_TYPE.ISOLATED:
                default:
                    valToRender = value[underling.name];
                    break;
            }
            var actuallySetValue = adptr.setValue(valToRender, silent);
            if (valToRender !== undefined && !silent) {
                adptr.emit("ValueRestored");
            }
            //If the actual set value is not returned
            if (actuallySetValue === undefined) {
                underling.formControlDefinition.value = value[underling.name];
            } else {
                underling.formControlDefinition.value = actuallySetValue;
            }
        } else {
            logger.error(
                underling,
                "(Conjurer._setUnderlingsValue) Can't set this underlings value because an adapter doesn't exist."
            );
        }
    };
    /**
     * Set the current _configurationSettings
     * @param configSettings {ConfigurationSettings} The configuration settings or type information needed to render/describe this form.
     * @throws {Error} if configSettings or optional configData is not a plainObject
     * @private
     */
    var _setConfigurationSettings = function (configSettings) {
        if (_.isPlainObject(configSettings)) {
            if (configSettings.hasOwnProperty("@")) {
                _configurationSettings = _.cloneDeep(configSettings);
            } else {
                _configurationSettings = _.cloneDeep(configSettings);
            }
        } else {
            var errMsg =
                "(Conjurer._setConfigurationSettings) param configSettings must be a plain JS object see docs.";
            logger.warn({configSettings: configSettings}, errMsg);
            throw new Error(errMsg);
        }
    };

    /**
     * Do some investigation on the ConfigSettings object, and return a summary object.
     * @returns {Array.<FormControlDefinition>} An array of Form Control Definitons that describe the current configuration settings.
     * @throws {Error} if the current _configurationSettings are malformed
     * @private
     */
    var _parseConfigSettings = function () {
        var summary = [];
        var tabFcd;
        var tabNum = 0;
        function parseProp(csPropDef, csPropName) {
            //If this is an ignored field, ignore it...
            if (csPropDef.fieldType === undefined) {
                return;
            }

            var fcd = _createFormControlDefinition(csPropName, csPropDef);

            //set refInstances
            fcd.refInstances = _refInstances;
            fcd.refTypes = _refTypes;

            if (fcd.type === CONTROL_TYPE.TAB_GROUP) {
                if (!tabFcd) {
                    tabFcd = _createFormControlDefinition(null, {fieldType: "TabGroup"});
                    tabFcd.tabName = "tab_" + Math.floor(Math.random() * (10000 - 0));
                    tabFcd.refInstances = _refInstances;
                    tabFcd.refTypes = _refTypes;
                    summary.push(tabFcd);
                }
                // summary.push(fcd);
                fcd.configSettings.fieldType = "Tab";
                fcd.configSettings.tabName = tabFcd.tabName;
                fcd.configSettings.tabNum = tabNum;
                tabNum++;
                tabFcd.tabs.push(fcd);
                tabFcd.configSettings.value["@propertyOrder"].push(fcd.name);
                tabFcd.configSettings.value[fcd.name] = fcd.configSettings;
            } else {
                summary.push(fcd);
            }
        }
        if (_configurationSettings) {
            //If this is an object
            if (_.isPlainObject(_configurationSettings)) {
                // This is a hack to inject the display info into an
                // object level annotation.
                if (_configurationSettings["@Complete"] && _configurationSettings["@Display"]) {
                    _configurationSettings["@Complete"].Display =
                        _configurationSettings["@Display"];
                }
                _orderedProps = Object.keys(_configurationSettings);
                //Make sure propertyOrder is defined
                if (_configurationSettings["@propertyOrder"]) {
                    _orderedProps = _configurationSettings["@propertyOrder"];
                }

                _layoutConfig = _.get(_configurationSettings, "@Display.layout", {});
                _styleConfig = _.get(_configurationSettings, "@Display.styles", {});

                if (_configurationSettings.hasOwnProperty("@")) {
                    parseProp(_configurationSettings, "");
                } else {
                    _orderedProps.forEach(function (csPropName) {
                        var csPropDef = _configurationSettings[csPropName];
                        parseProp(csPropDef, csPropName);
                    });
                }
            }
            //If this is an array
            else if (_.isArray(_configurationSettings)) {
                _configurationSettings.forEach(function (csPropDef) {
                    parseProp(csPropDef);
                });
            } else {
                var errMsg = "(Conjurer._parseConfigSettings) UNSUPPORTED Configuration Settings!";
                logger.warn({_configurationSettings: _configurationSettings}, errMsg);
                throw new Error(errMsg);
            }
        }
        return summary;
    };

    /**
     * Create a FormControlDefinition from a Configuration Setting
     * @param csName {string} The name of this configuration setting
     * @param csProperty {object} The configuration setting property
     * @returns {FormControlDefinition} The FormControlDefinition that describes the configuration setting.
     */
    var _createFormControlDefinition = function (csName, csProperty) {
        var fcd = {
            name: csName || "@VALUE",
            type: formRendererUtilities.getFormControlTypeFromConfigSettings(csProperty),
            configSettings: csProperty,
            displayInfo: {
                label: csName,
                tooltip: csName, //TODO<--- could be different based on the type
                placeholder: csName, //TODO<--- could be different based on the type
            },
        };
        //If a display property was specified in the _configurationSettings
        if (csProperty.Display) {
            //User specified label
            if (_.isString(csProperty.Display.label)) {
                fcd.displayInfo.label = csProperty.Display.label;
                fcd.displayInfo.tooltip = csProperty.Display.label;
                fcd.displayInfo.placeholder = csProperty.Display.label;
            }
            //User specified description
            if (csProperty.Display.description) {
                fcd.displayInfo.tooltip = csProperty.Display.description;
            }
            //User specified placeholder
            if (csProperty.Display.placeholder) {
                fcd.displayInfo.placeholder = csProperty.Display.placeholder;
            }
        }

        //validation Info
        if (csProperty.hasOwnProperty("Required")) {
            _.set(fcd, "validationInfo.required", csProperty["Required"]);
        }

        //setup the Adapters default value
        switch (csProperty.fieldType) {
            case "String":
                fcd.value = "";
                break;
            case "Object":
                fcd.value = {};
                break;
            case "TabGroup":
                fcd.tabs = [];
                fcd.configSettings = {
                    "@": "@",
                    value: {
                        "@propertyOrder": [],
                    },
                };
                break;
            case "Boolean":
                fcd.value = false;
                break;
            case "Array":
                fcd.value = [];
                break;
            case "Enum":
            case "Select":
            case "Number":
                fcd.value = null;
                break;
            case "Ref":
            case "Choice":
            default:
                fcd.value = undefined;
                break;
        }

        if (csProperty.hasOwnProperty("value")) {
            fcd.value = csProperty.value;
        }

        //Find the Adapter Class associated with this type
        if (_controlAdapterRegistry[fcd.type]) {
            var regEntry = _controlAdapterRegistry[fcd.type];
            let adapterClass = regEntry.adapterClass;
            if (_.isFunction(regEntry.adapterClassFactory)) {
                adapterClass = regEntry.adapterClassFactory(fcd);
            }
            fcd.adapterInfo = {
                type: regEntry.type,
                class: adapterClass,
                valueScope: regEntry.valueScope || VALUE_SCOPE_TYPE.ISOLATED,
            };
        } else {
            fcd.adapterInfo = {
                type: UNDERLING_TYPE.CONTROL_ADAPTER,
                class: _controlAdapterRegistry[CONTROL_TYPE.UNKNOWN].adapterClass,
                valueScope: VALUE_SCOPE_TYPE.ISOLATED,
            };
        }

        //Pass along the ControlAdapterRegistry
        fcd.adapterInfo.controlAdapterRegistry = _controlAdapterRegistry;

        return fcd;
    };
    /**
     * Set a property on _configurationData
     * @param propName {string} the configuration data property name.
     * @param val {*} The actual value
     * @private
     */
    var _setConfigDataProperty = function (propName, val) {
        _configurationData[propName] = val;
    };
    /**
     * The logic necessary to create the adapter's containing DOM element. This also contains
     * logic to attach the newly created containing DOM element to the parentElement.
     * @param parentElem {DomElement} The base element of the form being rendered. If this is a nested part of the form
     * this element will reference that part of the parent form.
     * @param fcd {FormControlDefinition} The form control definition that describes this control adapter
     * @private
     */
    var _createAdapterContainingElement = function (parentElem, fcd) {
        var newElem;
        //look to see if there is a createAdapterContainingElement for this ControlAdapter inside the Registry
        if (
            _controlAdapterRegistry[fcd.type] &&
            _.isFunction(_controlAdapterRegistry[fcd.type].createAdapterContainingElement)
        ) {
            newElem = _controlAdapterRegistry[fcd.type].createAdapterContainingElement(
                parentElem,
                fcd
            );
        } else {
            newElem = _conjurerOptions.createAdapterContainingElement(containerElem, fcd);
        }
        return newElem;
    };

    /**
     * Fire a Conjurer Value Changed Event
     * @param newValue
     * @param [evt] {
     */
    var fireValueChanged = function (newValue, evt) {
        //update the current _value with this controls change info
        self.emit("ValueChanged", {
            controlValueChangeEvent: evt,
            newValue: newValue,
            oldValue: _oldValue,
        });
        _oldValue = newValue;
    };

    var _fireMultiEditActive = function (newMultiEditActive, evt) {
        self.emit("MultiEditActive", {
            controlValueChangeEvent: evt,
            newValue: newMultiEditActive,
            oldValue: _oldMultiEditActive,
        });
        _oldMultiEditActive = self.getMultiEditActive();
    };

    var _fireInvalidValue = function (newValue, evt) {
        self.emit("Invalid", {
            controlValueChangeEvent: evt,
            newValue: newValue,
            oldValue: evt.oldValue,
        });
    };

    var _fireInProgress = function (newInProgress, evt) {
        self.emit("InProgress", {
            controlInProgressStatusChangeEvent: evt,
            newInProgressStatus: newInProgress,
            oldInProgressStatus: evt.oldInProgressStatus,
        });
    };

    /***************************************************************************************/
    /*** END PRIVATE METHODS                                                             ***/
    /*** START INITIALIZATION                                                            ***/
    /***************************************************************************************/
    //Setup defaults or use defined options
    if (_conjurerOptions) {
        if (_conjurerOptions.controlAdapterRegistry) {
            _controlAdapterRegistry = _conjurerOptions.controlAdapterRegistry;
        }
        if (_conjurerOptions.configurationData) {
            self.setConfigurationData(_conjurerOptions.configurationData);
        }
        if (_conjurerOptions.refInstances) {
            self.setRefInstances(_conjurerOptions.refInstances);
        }
        if (_conjurerOptions.refTypes) {
            self.setRefTypes(_conjurerOptions.refTypes);
        }
        if (_conjurerOptions.configurationSettings) {
            self.render(_conjurerOptions.configurationSettings);
        }
    }
    /***************************************************************************************/
    /*** END INITIALIZATION                                                              ***/
    /***************************************************************************************/
};
//Make this API inherit from EventEmitter
util.inherits(module.exports, EventEmitter);
